<template>

  <section class="skills-page">

    <!-- =====================================================
         HERO
    ====================================================== -->

    <div class="skills-card skills-hero">

      <div class="hero-glow hero-glow-blue"></div>
      <div class="hero-glow hero-glow-purple"></div>

      <div class="hero-content">

        <div class="hero-icon">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M9 4L7 20"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />

            <path
              d="M17 4L15 20"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />

            <path
              d="M4 9H20"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />

            <path
              d="M3 15H19"
              stroke="currentColor"
              stroke-width="1.7"
              stroke-linecap="round"
            />
          </svg>
        </div>


        <div class="hero-copy">

          <div class="eyebrow">
            Профессиональный профиль
          </div>

          <h2 class="hero-title">
            Расскажите, что вы умеете
          </h2>

          <p class="hero-description">
            Добавьте ключевые навыки и технологии.
            Они помогают Hr3 лучше понимать вашу специализацию
            и показывать более релевантные возможности.
          </p>

        </div>

      </div>


      <!-- =====================================================
           SUMMARY
      ====================================================== -->

      <div class="skills-summary">

        <div class="summary-icon">
          #
        </div>

        <div class="summary-copy">

          <div class="summary-value">
            {{ localHashtags.length }}
          </div>

          <div class="summary-label">
            {{ skillsLabel }}
          </div>

        </div>

      </div>

    </div>


    <!-- =====================================================
         EDITOR
    ====================================================== -->

    <div class="skills-card editor-card">

      <div class="section-heading">

        <div class="section-eyebrow">
          Навыки
        </div>

        <h3>
          Профессиональные скиллы
        </h3>

        <p>
          Выберите существующие теги или создайте собственные.
        </p>

      </div>


      <div
        class="hashtags-body"
        :class="{
          saving: isSaving
        }"
      >
        <HashtagsForProfile
          :initial-hashtags="initialHashtags"
          @save-hashtags="onInternalSave"
        />
      </div>


      <!-- =====================================================
           SAVE
      ====================================================== -->

      <div class="save-area">

        <div class="save-state">

          <span
            class="save-state-dot"
            :class="{ changed: isChanged }"
          ></span>

          {{
            isChanged
              ? "Есть несохранённые изменения"
              : "Все изменения сохранены"
          }}

        </div>


        <button
          type="button"
          class="save-btn"
          :class="{
            active: isChanged && !isSaving,
            saved: justSaved
          }"
          :disabled="!isChanged || isSaving"
          @click="save"
        >

          <span
            v-if="isSaving"
            class="button-spinner"
            aria-hidden="true"
          ></span>


          <svg
            v-else-if="justSaved"
            class="saved-icon"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M5 10.3L8.2 13.5L15 6.7"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>


          <span>
            {{ buttonText }}
          </span>

        </button>

      </div>

    </div>

  </section>

</template>


<script setup>

import {
  ref,
  computed,
  watch
} from "vue";

import UserService
  from "../../api/UserService";

import HashtagsForProfile
  from "../HashtagsForProfile.vue";


const emit = defineEmits([
  "saved"
]);


const props = defineProps({

  initialHashtags: {
    type: Array,
    default: () => []
  },

  userId: {
    type: String,
    required: true
  }

});


/* ============================================================
   STATE
============================================================ */

const localHashtags =
  ref(
    cloneHashtags(
      props.initialHashtags
    )
  );


const initialSnapshot =
  ref(
    createSnapshot(
      props.initialHashtags
    )
  );


const isSaving =
  ref(false);


const justSaved =
  ref(false);


let savedTimer =
  null;


/* ============================================================
   SYNC FROM PARENT
============================================================ */

watch(
  () => props.initialHashtags,

  value => {

    if (isSaving.value) {
      return;
    }


    /*
     * Если локально есть несохранённые изменения,
     * не перетираем их внезапным обновлением prop.
     */

    if (isChanged.value) {
      return;
    }


    localHashtags.value =
      cloneHashtags(value);


    initialSnapshot.value =
      createSnapshot(value);

  },

  {
    deep: true
  }
);


/* ============================================================
   CHILD CHANGES
============================================================ */

function onInternalSave(newTags) {

  if (!Array.isArray(newTags)) {
    return;
  }


  localHashtags.value =
    cloneHashtags(newTags);

}


/* ============================================================
   CHANGED
============================================================ */

const isChanged =
  computed(() => {

    return (
      createSnapshot(
        localHashtags.value
      )
      !==
      initialSnapshot.value
    );

  });


/* ============================================================
   LABEL
============================================================ */

const skillsLabel =
  computed(() => {

    const count =
      localHashtags.value.length;


    const mod100 =
      count % 100;

    const mod10 =
      count % 10;


    if (
      mod100 >= 11
      && mod100 <= 19
    ) {
      return "навыков";
    }


    if (mod10 === 1) {
      return "навык";
    }


    if (
      mod10 >= 2
      && mod10 <= 4
    ) {
      return "навыка";
    }


    return "навыков";

  });


/* ============================================================
   BUTTON
============================================================ */

const buttonText =
  computed(() => {

    if (isSaving.value) {
      return "Сохраняем…";
    }


    if (justSaved.value) {
      return "Готово";
    }


    return "Сохранить";

  });


/* ============================================================
   HELPERS
============================================================ */

function cloneHashtags(value = []) {

  return value.map(tag => {

    if (
      typeof tag === "string"
    ) {
      return tag;
    }


    return {
      ...tag
    };

  });

}


function normalizeHashtag(tag) {

  if (
    typeof tag === "string"
  ) {

    return {
      id: tag,
      name: tag
    };

  }


  return {

    id:
      tag?.id
      ?? tag?._id
      ?? tag?.name
      ?? "",

    name:
      tag?.name
      ?? tag?.hashtag
      ?? String(
        tag?.id
        ?? tag?._id
        ?? ""
      )

  };

}


function createSnapshot(value = []) {

  const normalized =
    value.map(
      normalizeHashtag
    );


  /*
   * Сравниваем независимо от порядка,
   * чтобы случайная перестановка тегов
   * не включала кнопку сохранения.
   */

  normalized.sort(
    (a, b) =>
      String(a.id)
        .localeCompare(
          String(b.id)
        )
  );


  return JSON.stringify(
    normalized
  );

}


/* ============================================================
   SAVE
============================================================ */

async function save() {

  if (
    !isChanged.value
    || isSaving.value
  ) {
    return;
  }


  if (!props.userId) {

    console.error(
      "❌ Не удалось определить пользователя"
    );

    return;
  }


  isSaving.value =
    true;

  justSaved.value =
    false;


  try {

    /*
     * Перед отправкой оставляем ту же структуру,
     * которую отдаёт HashtagsForProfile.
     */

    const hashtags =
      cloneHashtags(
        localHashtags.value
      );


    await UserService.updateUserProfile({

      user_id:
        props.userId,

      hashtags

    });


    /*
     * ВАЖНО:
     * props.initialHashtags больше не мутируем.
     *
     * Просто говорим:
     * текущее состояние теперь является эталоном.
     */

    initialSnapshot.value =
      createSnapshot(
        hashtags
      );


    justSaved.value =
      true;


    if (savedTimer) {
      clearTimeout(
        savedTimer
      );
    }


    savedTimer =
      setTimeout(() => {

        justSaved.value =
          false;

      }, 1200);


    emit(
      "saved",
      hashtags
    );


  } catch (error) {

    console.error(
      "❌ Ошибка при сохранении скиллов:",
      error
    );


  } finally {

    isSaving.value =
      false;

  }

}

</script>


<style scoped>

/* ============================================================
   ROOT
============================================================ */

.skills-page {
  width: 100%;

  display: flex;
  flex-direction: column;

  gap: 16px;

  font-family:
    "Eastman-Regular",
    sans-serif;
}


/* ============================================================
   BASE CARDS
============================================================ */

.skills-card {
  position: relative;

  width: 100%;

  box-sizing: border-box;

  overflow: hidden;

  background: #ffffff;

  border:
    1px solid
    rgba(
      20,
      35,
      60,
      0.09
    );

  border-radius: 24px;

  box-shadow:
    0
    16px
    45px
    rgba(
      15,
      23,
      42,
      0.055
    );
}


/* ============================================================
   HERO
============================================================ */

.skills-hero {
  padding: 28px;
}


.hero-content {
  position: relative;

  z-index: 2;

  display: flex;
  align-items: flex-start;

  gap: 15px;
}


.hero-icon {
  width: 48px;
  height: 48px;

  flex:
    0
    0
    48px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #407edc;

  background:
    linear-gradient(
      145deg,
      rgba(40, 135, 247, 0.12),
      rgba(124, 92, 231, 0.075)
    );

  border:
    1px solid
    rgba(
      40,
      120,
      230,
      0.11
    );

  border-radius: 15px;
}


.hero-icon svg {
  width: 23px;
  height: 23px;
}


.hero-copy {
  min-width: 0;
}


.eyebrow,
.section-eyebrow {
  margin-bottom: 5px;

  color: #9499a2;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  line-height: 1.2;

  letter-spacing: 0.07em;

  text-transform: uppercase;
}


.hero-title {
  margin: 0;

  color: #292e35;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 17px;

  line-height: 1.3;

  letter-spacing: -0.25px;
}


.hero-description {
  max-width: 570px;

  margin:
    6px
    0
    0;

  color: #80858e;

  font-size: 11px;

  line-height: 1.5;
}


/* ============================================================
   GLOW
============================================================ */

.hero-glow {
  position: absolute;

  width: 230px;
  height: 230px;

  pointer-events: none;

  border-radius: 50%;

  filter:
    blur(80px);

  opacity: 0.1;
}


.hero-glow-blue {
  top: -125px;
  right: -65px;

  background: #2887f7;
}


.hero-glow-purple {
  bottom: -170px;
  left: -100px;

  background: #7c5ce7;
}


/* ============================================================
   SUMMARY
============================================================ */

.skills-summary {
  position: relative;

  z-index: 2;

  width: fit-content;

  display: flex;
  align-items: center;

  gap: 9px;

  margin-top: 24px;

  padding:
    9px
    12px;

  background:
    rgba(
      247,
      249,
      252,
      0.82
    );

  border:
    1px solid
    rgba(
      25,
      40,
      60,
      0.06
    );

  border-radius: 12px;
}


.summary-icon {
  width: 28px;
  height: 28px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #477ccc;

  background:
    rgba(
      55,
      115,
      205,
      0.075
    );

  border-radius: 8px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 13px;
}


.summary-copy {
  display: flex;
  align-items: baseline;

  gap: 5px;
}


.summary-value {
  color: #3479dc;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 14px;
}


.summary-label {
  color: #9398a0;

  font-size: 8px;
}


/* ============================================================
   EDITOR
============================================================ */

.editor-card {
  padding:
    26px
    20px
    20px;
}


.section-heading {
  padding:
    0
    8px
    19px;
}


.section-heading h3 {
  margin: 0;

  color: #30353d;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 14px;

  font-weight: 500;
}


.section-heading p {
  margin:
    5px
    0
    0;

  color: #989da5;

  font-size: 9px;

  line-height: 1.4;
}


/* ============================================================
   HASHTAG CHILD
============================================================ */

.hashtags-body {
  padding:
    17px
    18px;

  background:
    rgba(
      248,
      249,
      251,
      0.64
    );

  border:
    1px solid
    rgba(
      25,
      40,
      60,
      0.06
    );

  border-radius: 16px;

  transition:
    opacity
    0.2s ease,
    background-color
    0.2s ease;
}


.hashtags-body.saving {
  opacity: 0.58;

  pointer-events: none;
}


/* ============================================================
   SAVE
============================================================ */

.save-area {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 20px;

  margin-top: 18px;

  padding:
    18px
    8px
    2px;

  border-top:
    1px solid
    rgba(
      25,
      40,
      60,
      0.065
    );
}


.save-state {
  display: inline-flex;
  align-items: center;

  gap: 6px;

  color: #9a9fa7;

  font-size: 8px;
}


.save-state-dot {
  width: 5px;
  height: 5px;

  background: #b9bdc3;

  border-radius: 50%;

  transition:
    background-color
    0.2s ease;
}


.save-state-dot.changed {
  background: #f2a23a;
}


/* ============================================================
   SAVE BUTTON
============================================================ */

.save-btn {
  min-width: 128px;
  min-height: 40px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 8px;

  padding:
    0
    16px;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #3187ee,
      #4968e6
    );

  border: none;
  border-radius: 12px;

  box-shadow:
    0
    7px
    17px
    rgba(
      40,
      111,
      225,
      0.2
    );

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;

  cursor: pointer;

  opacity: 0.4;

  transition:
    opacity
    0.2s ease,
    transform
    0.2s ease,
    box-shadow
    0.2s ease,
    background
    0.2s ease;
}


.save-btn.active {
  opacity: 1;
}


.save-btn.saved {
  opacity: 1;

  background:
    linear-gradient(
      135deg,
      #39b875,
      #299761
    );
}


.save-btn:hover:not(:disabled) {
  transform:
    translateY(-1px);

  box-shadow:
    0
    10px
    22px
    rgba(
      40,
      111,
      225,
      0.27
    );
}


.save-btn:active:not(:disabled) {
  transform:
    translateY(0)
    scale(0.98);
}


.save-btn:disabled {
  cursor: default;

  box-shadow: none;
}


.button-spinner {
  width: 14px;
  height: 14px;

  border:
    2px solid
    rgba(
      255,
      255,
      255,
      0.35
    );

  border-top-color: #ffffff;

  border-radius: 50%;

  animation:
    buttonSpin
    0.65s
    linear
    infinite;
}


.saved-icon {
  width: 15px;
  height: 15px;
}


@keyframes buttonSpin {

  to {
    transform:
      rotate(360deg);
  }

}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.skills-card {
  background:
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.07
    );

  box-shadow:
    0
    18px
    50px
    rgba(
      0,
      0,
      0,
      0.24
    );
}


html[data-theme="dark"]
.hero-title,
html[data-theme="dark"]
.section-heading h3 {
  color: #f0f1f3;
}


html[data-theme="dark"]
.hero-description,
html[data-theme="dark"]
.section-heading p {
  color: #8d929b;
}


html[data-theme="dark"]
.skills-summary {
  background:
    rgba(
      255,
      255,
      255,
      0.035
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.055
    );
}


html[data-theme="dark"]
.hashtags-body {
  background:
    rgba(
      255,
      255,
      255,
      0.025
    );

  border-color:
    rgba(
      255,
      255,
      255,
      0.055
    );
}


html[data-theme="dark"]
.save-area {
  border-color:
    rgba(
      255,
      255,
      255,
      0.065
    );
}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 700px) {

  .skills-page {
    gap: 13px;
  }


  .skills-hero {
    padding: 20px;

    border-radius: 20px;
  }


  .editor-card {
    padding:
      21px
      12px
      14px;

    border-radius: 20px;
  }


  .hashtags-body {
    padding:
      14px
      13px;

    border-radius: 14px;
  }


  .save-area {
    align-items: stretch;
    flex-direction: column;

    gap: 12px;
  }


  .save-btn {
    width: 100%;

    min-height: 42px;
  }

}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {

  .hashtags-body,
  .save-btn {
    transition: none;
  }


  .button-spinner {
    animation: none;
  }

}

</style>